/**
 * Right-hand pieces of the briefing (08 §2.3): the mission card - what, where, light, weather,
 * threat, conditions, reward, the intel held - and the north-up schematic of the area. The
 * schematic shows objective AREAS only: that is the fidelity the intel allows here. The card also
 * names the mission's category, prints its one-line summary and carries its FACTS tags (TIMED,
 * NO DROP, POD A, NO RECALL, QUIET), the same chips the theatre map's node card shows.
 */

import './briefing.css';
import '../widgets/dossier.css';
import { archetypeInfo, archetypeNumber, clampThreat } from '../archetypes';
import type { Ctx, MissionPlan } from '../context';
import { box, el, panel } from '../dom';
import { factChips } from '../missionTags';
import { multiplier } from '../text';
import { segmentGauge } from '../widgets/gauge';

function row(label: string, value: string): HTMLElement {
  return box('menu-brief-row', el('span', 'menu-dim', label), el('span', 'menu-paper', value));
}

/** Whose tags lie on this ground (04 §2.3), from the mission itself or from its map node. */
export function tagRecoveryName(plan: MissionPlan): string | null {
  return plan.params.tagRecovery?.name ?? plan.node?.tagRecovery?.name ?? null;
}

/** North-up schematic: objective area and pickup north, the approach, the drop band, the badlands. */
export function areaStrip(plan: MissionPlan): HTMLElement {
  const strip = el('div', 'menu-brief-strip');
  strip.setAttribute('role', 'img');
  strip.setAttribute(
    'aria-label',
    'AREA SCHEMATIC, NORTH UP: OBJECTIVE AREA AND PICKUP X TO THE NORTH, DROP BAND TO THE SOUTH',
  );
  const objective = box('menu-brief-zone menu-brief-zone-obj', el('span', '', 'OBJECTIVE AREA'));
  objective.append(el('span', 'menu-brief-x', 'X'));
  if (plan.params.intel.includes('alternate-lz')) objective.append(el('span', 'menu-brief-x menu-brief-x-alt', 'X'));
  strip.append(el('span', 'menu-brief-north', 'N'), objective);
  // Mission 6's obstacle spans the whole area: it is ground, not a structure, so it is shown.
  if (plan.params.archetype === 'radar-array') strip.append(box('menu-brief-zone menu-brief-zone-water', 'OBSTACLE'));
  strip.append(
    box('menu-brief-zone menu-brief-zone-gap', 'APPROACH'),
    box('menu-brief-zone menu-brief-zone-drop', 'DROP BAND'),
    box('menu-brief-zone menu-brief-zone-bad', 'BADLANDS'),
  );
  return strip;
}

function described(ctx: Ctx, heading: string, ids: readonly string[]): HTMLElement[] {
  if (ids.length === 0) return [];
  const out: HTMLElement[] = [el('div', 'menu-dossier-key', heading)];
  for (const id of ids) {
    const d = ctx.describe(id);
    const line = el('div', 'menu-brief-mod menu-prose', d.name);
    line.title = d.text;
    out.push(line);
  }
  return out;
}

export function missionCard(ctx: Ctx, plan: MissionPlan): HTMLElement {
  const p = plan.params;
  const node = plan.node;
  const card = panel('MISSION', 'menu-brief-card');
  const archetype = ctx.describe(p.archetype);
  const name = el('div', 'menu-brief-name', `${archetypeNumber(p.archetype)} ${archetype.name}`);
  name.title = archetype.text;

  const tags = el('div', 'menu-row menu-row-wrap');
  const variant = node !== null ? node.variant : archetypeInfo(p.archetype).biome !== p.biome;
  if (node?.keystone === true) tags.append(el('span', 'menu-chip menu-chip-amber', 'KEYSTONE'));
  if (variant) tags.append(el('span', 'menu-chip menu-chip-dim', 'VARIANT'));
  if (node?.kind === 'rescue') tags.append(el('span', 'menu-chip menu-chip-red', 'POW RESCUE'));
  if (tagRecoveryName(plan) !== null) tags.append(el('span', 'menu-chip menu-chip-amber', 'TAG RECOVERY'));
  tags.append(...factChips(ctx.describe, p.archetype));
  const summary = el('div', 'menu-brief-summary menu-prose menu-dim', archetype.text);

  const threat = segmentGauge('THREAT', 9, 7);
  threat.set(clampThreat(p.threat));
  card.body.append(
    name,
    tags,
    ...(archetype.text === '' ? [] : [summary]),
    row('CATEGORY', ctx.describe(`category:${archetypeInfo(p.archetype).category}`).name),
    row('TERRAIN', ctx.describe(p.biome).name),
    row('LIGHT', ctx.describe(p.timeOfDay).name),
    row('WEATHER', ctx.describe(p.weather).name),
    threat.el,
    // ROUND8: how many handlers and dogs the threat and the cards put on the ground.
    ...((p.dogTeams ?? 0) > 0 ? [row('DOG TEAMS', String(p.dogTeams))] : []),
    ...described(ctx, 'CONDITIONS', p.modifiers),
  );
  if (node !== null) card.body.append(row('REWARD', `${node.rewardRp} RP ${multiplier(node.rewardMultiplier)}`));
  card.body.append(...described(ctx, 'INTEL HELD', p.intel));
  return card.panel;
}
